import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  addCalendarDays,
  calendarDaysFrom,
  toLocalDateKey,
} from "../../features/utils/calendarDays";
import { formatDateRange } from "../../features/utils/displayFormat";

interface DatePickerProps {
  selectedDate?: Date;
  onDateSelect: (date: Date) => void;
  disabledDates?: string[]; // YYYY-MM-DD format
  className?: string;
  disabled?: boolean;
}

const STRIP_DAYS = 14;

const formatYMD = toLocalDateKey;

const stripDate = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

/**
 * Two-week horizontal strip date picker. Replaces the previous month-grid layout
 * because in the create-session flow the user almost always picks a date within
 * the next two weeks, and the old month-grid left ~⅔ of the row blank.
 *
 * Public API is unchanged: drop-in replacement for the previous DatePicker.
 */
const DatePicker: React.FC<DatePickerProps> = ({
  selectedDate,
  onDateSelect,
  disabledDates = [],
  className = "",
  disabled = false,
}) => {
  const { t, i18n } = useTranslation();
  const today = useMemo(() => stripDate(new Date()), []);

  const [stripStart, setStripStart] = useState<Date>(() => today);
  const [internalSelected, setInternalSelected] = useState<Date | null>(() => {
    if (!selectedDate) return today;
    const d = selectedDate instanceof Date ? selectedDate : new Date(selectedDate);
    return !isNaN(d.getTime()) ? stripDate(d) : today;
  });

  const dayShortNames = [
    t("sessionForm.days.sun"),
    t("sessionForm.days.mon"),
    t("sessionForm.days.tue"),
    t("sessionForm.days.wed"),
    t("sessionForm.days.thu"),
    t("sessionForm.days.fri"),
    t("sessionForm.days.sat"),
  ];

  const disabledSet = useMemo(
    () =>
      new Set(
        disabledDates.map((d) => {
          const date = new Date(d);
          return formatYMD(date);
        })
      ),
    [disabledDates]
  );

  // Hydrate from prop changes (edit mode loads).
  useEffect(() => {
    if (!selectedDate) return;
    const d = selectedDate instanceof Date ? selectedDate : new Date(selectedDate);
    if (isNaN(d.getTime())) return;
    const stripped = stripDate(d);
    setInternalSelected(stripped);
    // Anchor the strip so the selected date is visible: if it's outside the
    // current 14-day window, re-anchor to its start-of-week.
    const stripEnd = addCalendarDays(stripStart, STRIP_DAYS);
    if (stripped.getTime() < stripStart.getTime() || stripped.getTime() >= stripEnd.getTime()) {
      setStripStart(stripped);
    }
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select today's date if no selectedDate is provided (preserves prior behavior).
  useEffect(() => {
    if (!selectedDate) onDateSelect(today);
  }, [selectedDate, today]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calendar days, not 24 h steps: a clock-change day must appear exactly once.
  const days = useMemo(() => calendarDaysFrom(stripStart, STRIP_DAYS), [stripStart]);

  const headerLabel = formatDateRange(days[0], days[days.length - 1], i18n.language);

  const canGoPrev = !disabled && stripStart.getTime() > today.getTime();
  const canGoNext = !disabled;

  const handlePrev = () => {
    if (!canGoPrev) return;
    const newStart = addCalendarDays(stripStart, -STRIP_DAYS);
    // Clamp so we never go before today.
    setStripStart(newStart.getTime() < today.getTime() ? today : newStart);
  };

  const handleNext = () => {
    if (!canGoNext) return;
    setStripStart(addCalendarDays(stripStart, STRIP_DAYS));
  };

  const handleDateClick = (date: Date) => {
    if (disabled) return;
    if (date.getTime() < today.getTime()) return;
    if (disabledSet.has(formatYMD(date))) return;
    setInternalSelected(date);
    onDateSelect(date);
  };

  const isSelected = (date: Date): boolean =>
    !!internalSelected && stripDate(internalSelected).getTime() === date.getTime();

  const isToday = (date: Date): boolean => date.getTime() === today.getTime();

  return (
    <div className={`space-y-3 ${className}`} data-testid="date-picker">
      <div className="flex items-center justify-between">
        <p className="font-bold">{t("sessionForm.sessionDate")}</p>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-grey-30 dark:text-grey-90 hidden sm:block">
            {headerLabel}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handlePrev}
              disabled={!canGoPrev}
              aria-label={t("dates.previousTwoWeeks")}
              className="flex size-8 items-center justify-center rounded-full text-grey-30 hover:bg-grey-95 disabled:text-grey-70 disabled:cursor-not-allowed disabled:hover:bg-transparent dark:text-white dark:hover:bg-primary-90/10"
            >
              <ChevronLeft size={18} strokeWidth={2.5} />
            </button>
            <button
              type="button"
              onClick={handleNext}
              disabled={!canGoNext}
              aria-label={t("dates.nextTwoWeeks")}
              className="flex size-8 items-center justify-center rounded-full text-grey-30 hover:bg-grey-95 disabled:text-grey-70 disabled:cursor-not-allowed disabled:hover:bg-transparent dark:text-white dark:hover:bg-primary-90/10"
            >
              <ChevronRight size={18} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </div>

      <span className="text-sm font-medium text-grey-30 dark:text-grey-90 sm:hidden">
        {headerLabel}
      </span>

      <div className="grid grid-cols-7 gap-2 lg:grid-cols-[repeat(14,minmax(0,1fr))]">
        {days.map((date) => {
          const dateStr = formatYMD(date);
          const isPast = date.getTime() < today.getTime();
          const isUnavailable = isPast || disabledSet.has(dateStr) || disabled;
          const selected = isSelected(date);
          return (
            <button
              key={dateStr}
              type="button"
              onClick={() => handleDateClick(date)}
              disabled={isUnavailable}
              className={`flex flex-col items-center justify-center gap-0.5 rounded-xl border py-2 transition-colors ${
                selected
                  ? "border-primary-40 bg-primary-40 text-white dark:border-primary-90 dark:bg-primary-90 dark:text-primary-20"
                  : isUnavailable
                    ? "border-transparent text-grey-70 cursor-not-allowed"
                    : "border-grey-80 text-grey-20 hover:border-primary-40 hover:bg-primary-95 dark:border-grey-50 dark:text-white dark:hover:border-primary-70 dark:hover:bg-primary-30/30"
              } ${isToday(date) && !selected ? "ring-1 ring-primary-40/40 dark:ring-primary-70/40" : ""}`}
            >
              <span className="text-[11px] font-semibold uppercase opacity-80">
                {dayShortNames[date.getDay()]}
              </span>
              <span className="text-base font-semibold leading-none">
                {date.getDate()}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default DatePicker;
